/**
 * The composer's height-determining box.
 *
 * This module imports nothing, on purpose. `LaunchComposerSkeleton` is what
 * SSR paints on BOTH `/` and `/home` — each gates the real composer behind an
 * async client effect — so it has to learn how tall the editable is without
 * dragging `@tigerabrodioss/fude`, `SmartTextbox` or `TextShimmer` into the
 * first-paint chunk. For the same reason this is not re-exported from
 * `prompt-input/index.tsx`: that barrel would hand the heavy graph back.
 *
 * `launch-composer-chrome.ts` already owns the frame and the card for exactly
 * this reason. This is the piece that escaped it: the editable moved to
 * `min-h-24`, the hand-mirrored skeleton stayed at `min-h-16`, and the hero
 * was 32px short on every load of both pages until 2026-09.
 *
 * The paddings and leading ride along with the floor so the first line of
 * placeholder text lands in the same place across the swap, not just the box.
 */
export const PROMPT_INPUT_TEXTAREA_BOX_CLASSNAME =
  'min-h-24 w-full px-5 pt-4 pb-3 text-sm leading-6'

/**
 * Grow with the content up to a cap, then scroll inside the box.
 *
 * The editable surface is a `contenteditable` div, which has no intrinsic
 * height limit, so a long paste used to stretch it without bound. The chat
 * thread pins the composer `shrink-0` under a `flex-1 min-h-0` message list
 * inside an `overflow-hidden` shell, so the overrun crushed the message list
 * and pushed the submit button out of the shell — leaving no way to send what
 * had just been pasted. The floor in `PROMPT_INPUT_TEXTAREA_BOX_CLASSNAME`
 * still wins (CSS resolves min-height over max-height).
 *
 * `dvh` rather than a fixed height alone so the cap also shrinks on short
 * viewports; `overscroll-contain` keeps a flick at either end from scrolling
 * the message list behind it.
 */
export const PROMPT_INPUT_TEXTAREA_HEIGHT_CAP_CLASSNAME =
  'max-h-[min(40dvh,20rem)] overflow-y-auto overscroll-contain'

/**
 * The editable as a plain `InputGroupTextarea`: the composer while it is
 * disabled, and a composer shell without the Fude editor (the product demo).
 */
export const PROMPT_INPUT_PLAIN_TEXTAREA_CLASSNAME = `${PROMPT_INPUT_TEXTAREA_BOX_CLASSNAME} field-sizing-content focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${PROMPT_INPUT_TEXTAREA_HEIGHT_CAP_CLASSNAME}`
